// Static game content: businesses, customers, tips, achievements, supplies
import {
  Dog,
  Baby,
  Scissors,
  Trees,
} from "lucide-react";

export const BUSINESSES = {
  dog_walker: {
    id: "dog_walker",
    name: "Dog Walker",
    tagline: "Take furry pals for a wag-tastic walk!",
    icon: Dog,
    emoji: "🐕",
    primary: "#0284C7",
    lightBg: "bg-sky-100",
    borderColor: "border-sky-400",
    ringColor: "ring-sky-400",
    textColor: "text-sky-700",
    solidBg: "bg-sky-500",
    hoverBg: "hover:bg-sky-600",
    priceRange: [8, 25],
    baseCost: 2, // treats, poop bags
    minigameLabel: "Walk the Dog",
  },
  babysitter: {
    id: "babysitter",
    name: "Babysitter",
    tagline: "Keep little kids safe, fed and giggling while parents are out.",
    icon: Baby,
    emoji: "🧸",
    primary: "#8B5CF6",
    lightBg: "bg-violet-100",
    borderColor: "border-violet-400",
    ringColor: "ring-violet-400",
    textColor: "text-violet-700",
    solidBg: "bg-violet-500",
    hoverBg: "hover:bg-violet-600",
    priceRange: [10, 30],
    baseCost: 3, // snacks, crayons
    minigameLabel: "Watch the Kids",
  },
  craft_maker: {
    id: "craft_maker",
    name: "Craft Maker",
    tagline: "Make cool bracelets, cards & keychains to sell.",
    icon: Scissors,
    emoji: "🎨",
    primary: "#EC4899",
    lightBg: "bg-pink-100",
    borderColor: "border-pink-400",
    ringColor: "ring-pink-400",
    textColor: "text-pink-700",
    solidBg: "bg-pink-500",
    hoverBg: "hover:bg-pink-600",
    priceRange: [5, 20],
    baseCost: 4, // beads, string, paper
    minigameLabel: "Make the Craft",
  },
  lawn_mower: {
    id: "lawn_mower",
    name: "Lawn Mower",
    tagline: "Trim yards, earn green!",
    icon: Trees,
    emoji: "🌱",
    primary: "#16A34A",
    lightBg: "bg-green-100",
    borderColor: "border-green-400",
    ringColor: "ring-green-400",
    textColor: "text-green-700",
    solidBg: "bg-green-500",
    hoverBg: "hover:bg-green-600",
    priceRange: [15, 40],
    baseCost: 5, // fuel, blade wear
    minigameLabel: "Mow the Lawn",
  },
};

// Customer request pool - generator picks random ones per business
export const CUSTOMERS = {
  dog_walker: [
    { name: "Mrs. Parker", pet: "Buddy the Labrador 🐕", note: "Loves fetch, pulls a little on the leash.", size: "big", suggestedPrice: 15 },
    { name: "Mr. Chen", pet: "Pixel the Chihuahua 🐶", note: "Tiny and quick! Short walks only.", size: "small", suggestedPrice: 10 },
    { name: "Jenna R.", pet: "Rocky the Husky 🐺", note: "High energy — needs a long run.", size: "big", suggestedPrice: 22 },
    { name: "The Smiths", pet: "Daisy the Poodle 🐩", note: "Very well trained. Easy job!", size: "medium", suggestedPrice: 14 },
    { name: "Coach Alex", pet: "Bruno the Bulldog 🐶", note: "Slow strolls, gets tired fast.", size: "medium", suggestedPrice: 12 },
    { name: "Neighbor Sam", pet: "Ziggy the Beagle 🐶", note: "Saw your flyer! Sniffs everything.", size: "medium", suggestedPrice: 14 },
    { name: "Aunt Bea", pet: "Max the Corgi 🐕", note: "Short legs, big heart. Easy walk.", size: "small", suggestedPrice: 11 },
  ],
  babysitter: [
    { name: "The Nguyens", pet: "Lily, age 3 👧", note: "Loves puzzles. Bedtime story at 7.", size: "easy", suggestedPrice: 14 },
    { name: "Coach Ramirez", pet: "Twins Max & Mia, age 4 👦👧", note: "Two kids = busy! Snack at 4pm.", size: "big", suggestedPrice: 22 },
    { name: "Mrs. Patel", pet: "Baby Arjun, 18 months 👶", note: "Naps at 2. Keep outlets covered!", size: "medium", suggestedPrice: 18 },
    { name: "Dr. Kim", pet: "Noah, age 5 🧒", note: "Dinosaur fan. No screens after 6.", size: "easy", suggestedPrice: 15 },
    { name: "The Garcias", pet: "Sofia, age 2 👧", note: "Needs help with potty. Loves bubbles.", size: "medium", suggestedPrice: 17 },
    { name: "Mr. Okafor", pet: "Zara, age 4 👧", note: "Found your flyer! Loves dress-up.", size: "easy", suggestedPrice: 14 },
    { name: "Aunt Rosa", pet: "Cousins Leo & Eli 👦👦", note: "Energetic! Play tag in the yard.", size: "big", suggestedPrice: 24 },
  ],
  craft_maker: [
    { name: "Ella (school friend)", pet: "Friendship Bracelet 🧵", note: "Wants pink & purple, size medium.", size: "easy", suggestedPrice: 8 },
    { name: "Uncle Ray", pet: "Birthday Card 💌", note: "Handmade card for his wife.", size: "easy", suggestedPrice: 6 },
    { name: "The School Fair", pet: "5 Keychains 🔑", note: "Bulk order — set a fair price!", size: "hard", suggestedPrice: 22 },
    { name: "Baby Shower", pet: "Origami Set 🦢", note: "10 folded swans for decorations.", size: "medium", suggestedPrice: 15 },
    { name: "Neighbor Mia", pet: "Beaded Necklace 📿", note: "Rainbow beads, adjustable.", size: "medium", suggestedPrice: 12 },
    { name: "Teacher Ms. Fox", pet: "Thank-you Cards 💌", note: "Saw your flyer! 4 cards for helpers.", size: "medium", suggestedPrice: 14 },
    { name: "Cousin Theo", pet: "Gamer Keychain 🎮", note: "Something blue and cool, please.", size: "easy", suggestedPrice: 7 },
  ],
  lawn_mower: [
    { name: "Old Mr. Hopps", pet: "Small front yard 🌱", note: "Quick job. Just the front!", size: "small", suggestedPrice: 18 },
    { name: "Big Blue House", pet: "Half-acre lawn 🌳", note: "Big backyard. Watch for the sprinkler!", size: "big", suggestedPrice: 35 },
    { name: "Coach Beltra", pet: "Medium yard 🏡", note: "Flower beds around the edges — be careful.", size: "medium", suggestedPrice: 25 },
    { name: "The Park Family", pet: "Corner lot 🌾", note: "Two sides to mow.", size: "big", suggestedPrice: 32 },
    { name: "Ms. Delaney", pet: "Compact yard 🌼", note: "Small but rocky — dodge stones.", size: "small", suggestedPrice: 20 },
    { name: "New Neighbors", pet: "Overgrown yard 🌿", note: "Just moved in and saw your flyer. Tall grass!", size: "big", suggestedPrice: 30 },
    { name: "Mr. Grant", pet: "Side strip 🌾", note: "Narrow strip by the fence. Quick!", size: "small", suggestedPrice: 16 },
  ],
};

// Learning tips that pop up during play
export const LEARNING_TIPS = [
  {
    id: "profit",
    topic: "Profit",
    icon: "💰",
    title: "What is profit?",
    body: "Profit = Money you earn − Money you spent on supplies. If you earned $20 but spent $5 on treats, your profit is $15!",
    quiz: {
      q: "You earned $30 and spent $8 on supplies. What's your profit?",
      options: ["$38", "$22", "$30"],
      answer: 1,
      reward: 5,
    },
  },
  {
    id: "pricing",
    topic: "Pricing",
    icon: "🏷️",
    title: "How to set a price",
    body: "Charge enough to cover your costs AND make profit. Bigger jobs = higher price. But too high and customers say no!",
    quiz: {
      q: "A big yard with lots of grass should cost:",
      options: ["The same as a small yard", "More than a small yard", "Free!"],
      answer: 1,
      reward: 5,
    },
  },
  {
    id: "safety",
    topic: "Safety",
    icon: "🛡️",
    title: "Safety first!",
    body: "Always tell a parent where you're going. Never enter a stranger's house alone. Wear proper gear (gloves, closed-toe shoes).",
    quiz: {
      q: "Before starting a job at someone's house, you should:",
      options: ["Just show up", "Tell a parent/guardian", "Bring a friend secretly"],
      answer: 1,
      reward: 5,
    },
  },
  {
    id: "customer_service",
    topic: "Customers",
    icon: "⭐",
    title: "Happy customers = more work",
    body: "Say hi, be on time, and smile! Happy customers give you 5-star ratings and tell their friends about you.",
    quiz: {
      q: "The best way to get more customers is:",
      options: ["Ignore reviews", "Be reliable & kind", "Charge more"],
      answer: 1,
      reward: 5,
    },
  },
  {
    id: "savings",
    topic: "Savings",
    icon: "🏦",
    title: "Piggy Bank power",
    body: "Save a little from each job! Money in your piggy bank grows over time and helps you buy better supplies to earn even more.",
    quiz: {
      q: "Why save money in a piggy bank?",
      options: ["To lose it", "To grow it & buy upgrades", "It's boring"],
      answer: 1,
      reward: 5,
    },
  },
  {
    id: "expenses",
    topic: "Expenses",
    icon: "🧾",
    title: "Every business has costs",
    body: "Expenses are things you must pay for to work — like leashes, treats, beads, or lawnmower fuel. Track them!",
    quiz: {
      q: "Which of these is a business expense?",
      options: ["Buying craft beads to sell bracelets", "Winning the lottery", "Eating cereal"],
      answer: 0,
      reward: 5,
    },
  },
  {
    id: "marketing",
    topic: "Marketing",
    icon: "📣",
    title: "Spread the word",
    body: "Customers can't hire you if they don't know you exist! Marketing = telling people about your business. Three kid-friendly ways: 1) Flyers — hand them to neighbors WITH a grown-up. 2) Word of mouth — ask happy customers to tell their friends. 3) Referrals — offer $1 off to anyone who brings a new customer. Marketing costs a little now but brings more jobs later.",
    quiz: {
      q: "A happy customer tells their neighbor about you. That's called:",
      options: ["Word of mouth", "An expense", "A discount"],
      answer: 0,
      reward: 5,
    },
  },
];

export const FLYER_PACKS = [
  { id: "small", name: "Starter Pack", flyers: 5, price: 3, emoji: "📄" },
  { id: "medium", name: "Street Pack", flyers: 10, price: 5, emoji: "📑" },
  { id: "large", name: "Block Pack", flyers: 20, price: 8, emoji: "📚", tag: "Best value" },
];

export const FLYER_DEFAULTS = {
  dog_walker: { services: ["30-minute walks", "Fetch & playtime", "Fresh water refills"], slogan: "Happy pups, happy owners!" },
  babysitter: { services: ["Games, crafts & stories", "Snacks & bedtime routine", "Safety first, always"], slogan: "Happy kids, relaxed parents!" },
  craft_maker: { services: ["Friendship bracelets", "Handmade cards", "Custom keychains"], slogan: "Made with love, just for you!" },
  lawn_mower: { services: ["Front & back yard mowing", "Edge trimming", "Grass cleanup"], slogan: "Trim yards, big smiles!" },
};

export const SUPPLIES = [
  { id: "premium_leash", name: "Premium Leash", business: "dog_walker", price: 15, emoji: "🦮", boost: "+$3 per walk", boostValue: 3 },
  { id: "dog_treats", name: "Yummy Treats", business: "dog_walker", price: 8, emoji: "🦴", boost: "+1⭐ per job", boostValue: 0, ratingBoost: 1 },
  { id: "toy_kit", name: "Activity Kit", business: "babysitter", price: 10, emoji: "🎲", boost: "+$3 per visit", boostValue: 3 },
  { id: "first_aid", name: "Mini First-Aid Kit", business: "babysitter", price: 9, emoji: "🩹", boost: "+1⭐ per job", boostValue: 0, ratingBoost: 1 },
  { id: "bead_kit", name: "Deluxe Bead Kit", business: "craft_maker", price: 20, emoji: "📿", boost: "+$4 per craft", boostValue: 4 },
  { id: "craft_paper", name: "Fancy Paper Pack", business: "craft_maker", price: 7, emoji: "✂️", boost: "+1⭐ per job", boostValue: 0, ratingBoost: 1 },
  { id: "sharp_blade", name: "Sharp Mower Blade", business: "lawn_mower", price: 25, emoji: "🔪", boost: "+$5 per lawn", boostValue: 5 },
  { id: "gas_can", name: "Full Gas Can", business: "lawn_mower", price: 12, emoji: "⛽", boost: "-$2 supply cost", boostValue: 0, costCut: 2 },
];

export const ACHIEVEMENTS = [
  { id: "first_dollar", title: "First Dollar", desc: "Complete your first job", icon: "💵" },
  { id: "hundred_club", title: "$100 Club", desc: "Earn $100 total", icon: "💯" },
  { id: "five_star", title: "5-Star Kid", desc: "Reach 5-star reputation", icon: "⭐" },
  { id: "saver", title: "Super Saver", desc: "Save $50 in your Piggy Bank", icon: "🏦" },
  { id: "all_businesses", title: "Business Boss", desc: "Try all 4 businesses", icon: "🏆" },
  { id: "safe_pro", title: "Safety Pro", desc: "Answer 3 safety quizzes right", icon: "🛡️" },
  { id: "marketer", title: "Marketing Whiz", desc: "Get 3 new customers from flyers", icon: "📣" },
  { id: "top_boss", title: "Top Boss", desc: "Beat Riley in a weekly Top Boss race", icon: "🏆" },
];

export const RIVAL = {
  name: "Riley",
  avatar: { look: "boy", skin: 3, hairStyle: "fade", hairColor: 0, outfit: "jersey", outfitColor: 1, accessory: "cap", pet: "turtle", petName: "Zoom" },
  quips: [
    "Fair prices win customers. Watch and learn!",
    "My 5-star reviews do the talking.",
    "Beat my week and I'll bring you lemonade.",
    "Slow week? Neighbors called ME instead.",
  ],
};

const hash = (str) => str.split("").reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 9973, 7);

// Riley's price for the same job: a little around the suggested price, stable per job
export const rivalPriceFor = (job) => job.suggestedPrice - 2 + (hash(job.name + job.pet) % 4);

// Chance (0-0.85) that the customer picks the rival instead of you
export const rivalStealChance = ({ price, rivalPrice, playerRep, rivalRep }) => {
  const priceGap = price - rivalPrice;
  const repGap = playerRep - rivalRep;
  let chance = 0;
  if (priceGap > 0) chance += Math.min(0.6, priceGap * 0.12);
  if (repGap < 0) chance += Math.min(0.4, -repGap * 0.3);
  if (repGap >= 0.5) chance -= 0.2;
  if (priceGap <= 0 && repGap >= 0) chance = 0;
  return Math.max(0, Math.min(0.85, chance));
};

export const WEEK_LENGTH = 7;

// Generate a batch of customer requests (extra = leads from flyers)
export const generateJobs = (businessId, day = 1, extra = 0) => {
  const pool = CUSTOMERS[businessId] || [];
  const count = Math.min(3 + extra, pool.length);
  const shuffled = [...pool].sort(() => Math.random() - 0.5).slice(0, count);
  return shuffled.map((c, i) => ({
    ...c,
    id: `${businessId}_d${day}_${i}_${Date.now()}`,
    businessId,
    fromFlyer: i >= 3,
  }));
};
